import { Head, router } from "@inertiajs/react";
import { Download, Loader2, Network } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  type Stats,
  StatsGrid,
} from "@/bizminer/Admin/KpiModelNodes/_components/StatCard.tsx";
import BizminerAdminLayout from "@/bizminer/layouts/admin/BizminerAdminLayout.tsx";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  bizminer_admin_kpi_model_nodes_path,
  children_bizminer_admin_kpi_model_node_path,
  export_bizminer_admin_kpi_model_nodes_path,
} from "@/routes";
import type {
  KpiModelNode,
  KpiModelTree as KpiModelTreeType,
  KpiModelTreeVersion,
} from "@/types/serializers";
import KpiModelSearch from "./_components/KpiModelSearch";
import KpiModelTreeView, {
  type KpiModelTreeRef,
} from "./_components/KpiModelTree";
import NodeDetailPanel from "./_components/NodeDetailPanel";
import { VersionTabs } from "./_components/VersionTabs";

interface Props {
  trees: KpiModelTreeType[];
  currentTree: KpiModelTreeType | null;
  currentVersion: KpiModelTreeVersion | null;
  nodes: KpiModelNode[];
  stats: Stats;
}

interface SelectedNode {
  id: number;
  ancestorIds: number[];
}

interface PageHeaderProps {
  children: React.ReactNode;
  actions?: React.ReactNode;
}

const PageHeader = ({ children, actions }: PageHeaderProps) => (
  <header className="bg-card shadow shrink-0 px-4 sm:px-6 lg:px-8">
    <div className="flex justify-between items-center py-4">
      <div className="flex items-center gap-4">{children}</div>
      {actions}
    </div>
  </header>
);

interface TreeSelectorProps {
  trees: KpiModelTreeType[];
  currentTree: KpiModelTreeType | null;
  onChange: (treeId: string) => void;
}

const TreeSelector = ({ trees, currentTree, onChange }: TreeSelectorProps) => {
  if (trees.length <= 1) return null;

  return (
    <Select value={currentTree?.id?.toString() ?? ""} onValueChange={onChange}>
      <SelectTrigger className="w-[280px]">
        <SelectValue placeholder="Select a tree" />
      </SelectTrigger>
      <SelectContent>
        {trees.map((tree) => (
          <SelectItem key={tree.id} value={tree.id.toString()}>
            {tree.name}
            {tree.active_version && (
              <span className="ml-2 text-muted-foreground text-xs">
                ({tree.active_version.name})
              </span>
            )}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default function Index({
  trees,
  currentTree,
  currentVersion,
  nodes,
  stats,
}: Props) {
  const treeRef = useRef<KpiModelTreeRef>(null);
  const [selectedNode, setSelectedNode] = useState<SelectedNode | null>(null);

  const loadChildren = useCallback(async (nodeId: number) => {
    const response = await fetch(
      children_bizminer_admin_kpi_model_node_path(nodeId),
    );
    return response.json();
  }, []);

  // Focus on selected node when layout changes
  useEffect(() => {
    if (selectedNode) {
      // Small delay to let the new tree component initialize
      const timer = setTimeout(() => {
        treeRef.current?.expandToNode(
          selectedNode.ancestorIds,
          selectedNode.id,
        );
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [selectedNode]);

  const handleTreeChange = (treeId: string) => {
    setSelectedNode(null);
    router.visit(bizminer_admin_kpi_model_nodes_path({ tree_id: treeId }), {
      preserveState: false,
    });
  };

  const handleVersionChange = (versionId: string) => {
    setSelectedNode(null);
    router.visit(
      bizminer_admin_kpi_model_nodes_path({
        tree_id: currentTree?.id,
        version_id: versionId,
      }),
      { preserveState: false },
    );
  };

  const DownloadButton = ({
    currentVersion,
  }: {
    currentVersion: KpiModelTreeVersion | null;
  }) => {
    const isPending = currentVersion?.export_ready === false;

    return (
      <Button
        variant="outline"
        size="sm"
        onClick={handleExport}
        disabled={!currentVersion?.id || !currentVersion?.export_ready}
      >
        {isPending ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <Download className="h-4 w-4 mr-2" />
        )}
        Export
      </Button>
    );
  };

  const handleExport = () => {
    if (!currentVersion?.id) return;
    window.open(
      export_bizminer_admin_kpi_model_nodes_path({
        version_id: currentVersion.id,
      }),
      "_blank",
    );
  };

  return (
    <BizminerAdminLayout>
      <Head title="KPI Model - Admin - BizMinr" />
      <div className="flex flex-col h-[calc(100vh-4rem)] -m-4 lg:-m-6 overflow-hidden">
        <PageHeader
          actions={
            <div className="flex items-center gap-2">
              <DownloadButton currentVersion={currentVersion} />

              <KpiModelSearch
                treeId={currentTree?.id}
                versionId={currentVersion?.id}
                onSelect={(result) => {
                  setSelectedNode({
                    id: result.id,
                    ancestorIds: result.ancestor_ids,
                  });
                  treeRef.current?.expandToNode(result.ancestor_ids, result.id);
                }}
              />
            </div>
          }
        >
          <Network className="h-8 w-8 text-primary" />
          <h1 className="text-2xl font-bold text-foreground">KPI Model</h1>
          <TreeSelector
            trees={trees}
            currentTree={currentTree}
            onChange={handleTreeChange}
          />
        </PageHeader>

        <div className="flex-1 flex flex-col p-4 overflow-hidden min-h-0">
          {currentTree && currentTree.versions.length > 1 && (
            <div className="mb-4">
              <VersionTabs
                versions={currentTree.versions}
                currentVersionId={currentVersion?.id ?? null}
                onChange={handleVersionChange}
              />
            </div>
          )}

          <StatsGrid stats={stats} />

          <div className="flex-1 min-h-0 relative">
            <KpiModelTreeView
              ref={treeRef}
              data={nodes}
              onNodeClick={(nodeId, ancestorIds) =>
                setSelectedNode({ id: nodeId, ancestorIds })
              }
              onLoadChildren={loadChildren}
            />

            {selectedNode && (
              <NodeDetailPanel
                nodeId={selectedNode.id}
                onClose={() => {
                  setSelectedNode(null);
                  treeRef.current?.clearSelection();
                }}
                onNodeSelect={(ancestorIds, nodeId) => {
                  setSelectedNode({ id: nodeId, ancestorIds });
                  treeRef.current?.expandToNode(ancestorIds, nodeId);
                }}
                onFocus={(ancestorIds, nodeId) => {
                  treeRef.current?.expandToNode(ancestorIds, nodeId);
                }}
              />
            )}
          </div>
        </div>
      </div>
    </BizminerAdminLayout>
  );
}
